import { memo, useMemo } from 'react'

import { FolderItem } from '@inno-sdks/files'
import { CellTypes, Column, Row, Table } from '@inno-ui/table'
import { useGetFileItem } from 'modules/desktop/modules/files-integration/api'
import {
  formatDateTimeWithLocale,
  getUserAvatarUrl
} from 'modules/desktop/modules/files-integration/utils'
import { useTranslation } from 'react-i18next'

import { useItemModal } from '../../hooks/useItemModal'
import styles from '../Properties.module.scss'
import { useGetPropertyNameColumn } from '../hooks'

enum TableColumnIds {
  Property = 'property',
  Value = 'value'
}

const columns: Array<Column> = [
  {
    id: TableColumnIds.Property,
    label: '',
    isResizeable: false,
    initialWidth: 40
  },
  {
    id: TableColumnIds.Value,
    label: '',
    isResizeable: false
  }
]

export const FolderProperties = memo(() => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop'
  })

  const { itemId } = useItemModal()
  const { data: item } = useGetFileItem<FolderItem>(itemId)

  const getPropertyNameColumn = useGetPropertyNameColumn()

  const rows = useMemo<Array<Row>>(
    () => [
      {
        id: 'files-count',
        cells: [
          getPropertyNameColumn('files-count'),
          {
            type: CellTypes.Text,
            columnId: TableColumnIds.Value,
            props: { value: t('files', { count: item?.filesCount }) }
          }
        ]
      },
      ...((item?.sharedBy
        ? [
            {
              id: 'shared-by',
              cells: [
                getPropertyNameColumn('shared-by'),
                {
                  type: CellTypes.Person,
                  columnId: TableColumnIds.Value,
                  props: {
                    name: item.sharedBy.name,
                    avatar: getUserAvatarUrl(item.sharedBy.avatar)
                  }
                }
              ]
            }
          ]
        : []) as Array<Row>),
      {
        id: 'created-by',
        cells: [
          getPropertyNameColumn('created-by'),
          {
            type: CellTypes.Person,
            columnId: TableColumnIds.Value,
            props: {
              name: item?.createdBy?.name ?? 'N\\A',
              avatar: getUserAvatarUrl(item?.createdBy?.avatar)
            }
          }
        ]
      },
      {
        id: 'created-at',
        cells: [
          getPropertyNameColumn('created-at'),
          {
            type: CellTypes.Text,
            columnId: TableColumnIds.Value,
            props: {
              value: formatDateTimeWithLocale(item?.createdAt ?? new Date())
            }
          }
        ]
      },
      {
        id: 'updated-at',
        cells: [
          getPropertyNameColumn('updated-at'),
          {
            type: CellTypes.Text,
            columnId: TableColumnIds.Value,
            props: {
              value: formatDateTimeWithLocale(item?.updatedAt ?? new Date())
            }
          }
        ]
      }
    ],
    [item, getPropertyNameColumn, t]
  )

  if (!item) {
    return null
  }

  return (
    <div className={styles.properties}>
      <div className={styles.table}>
        <Table
          id='properties'
          columns={columns}
          rows={rows}
          withHeader={false}
        />
      </div>
    </div>
  )
})
